import { useRef, useState } from 'react';
import PratusInput from '../../../../DesignSystems/New/PratusInput';
import StylishNewComboBox, {
  type ComboBoxOption,
} from '../../../../DesignSystems/New/StylishNewComboBox';
import { TACTICS_LIBRARY } from '../ostLibraryData';
import { StylishNewButton } from '../../../../DesignSystems/New/StylishNewButton';
import { SharedIcon } from '../../../../SharedIcon/SharedIcon';
import { IncidentStrategy } from 'components/IAP/hooks/incidentStrategyHooks';
import classNames from 'classnames';
import { useCreateIncidentTactic } from '../../../hooks/incidentTacticHooks';
import { useEditIncidentStrategy } from '../../../hooks/incidentStrategyHooks';

type StrategyItemProps = {
  item: IncidentStrategy;
  selectedTactics: string[];
};

export const StrategyItem: React.FC<
  React.PropsWithChildren<StrategyItemProps>
> = ({ item, children, selectedTactics }) => {
  const [expanded, setExpanded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);

  const itemRef = useRef<HTMLDivElement | null>(null);
  const createTactic = useCreateIncidentTactic();
  const editStrategy = useEditIncidentStrategy();

  const isDisabled = Array.isArray(children) && children.length > 0;
  const isSelected =
    Array.isArray(children) &&
    children.some(
      (grandchild: React.ReactElement) =>
        grandchild?.props?.item?.tactic_id &&
        selectedTactics.includes(grandchild.props.item.tactic_id)
    );

  const handleEdit = (value: string) => {
    editStrategy.mutate({ ...item, name: value });
  };

  const handleDelete = () => {
    editStrategy.mutate({
      ...item,
      archived: true,
    });
  };

  const handleCreateTactic = (tacticDescription: string) => {
    createTactic.mutate({
      name: '',
      description: tacticDescription,
      strategies: [item.strategy_id],
      incident_id: item.incident_id,
    });
  };

  return (
    <div className="mb-2" ref={itemRef}>
      <div
        role="button"
        className={classNames('pratus-collapsible-item strategy-item', {
          'align-items-start': editing,
          corner: expanded,
        })}
        onClick={() => {
          if (!editing && !isSelected) {
            setExpanded(!expanded);
          }
        }}
      >
        {!editing ? (
          <>{item.name}</>
        ) : (
          <PratusInput
            defaultValue={item.name}
            showErrorContainer={false}
            className="flex-grow-1 me-2"
            onSave={(value: string) => {
              handleEdit(value);
              setEditing(false);
            }}
            onCancel={() => {
              setEditing(false);
            }}
          />
        )}
        <button
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: 'transparent',
            boxShadow: 'none',
            border: 'none',
          }}
        >
          <StylishNewButton
            className="button--link"
            onClick={() => {
              setEditing(!editing);
            }}
          >
            <SharedIcon iconName="edit" color="light" />
          </StylishNewButton>
          <StylishNewButton
            className="button--link"
            onClick={() => {
              handleDelete();
            }}
            disabled={isDisabled}
          >
            <SharedIcon
              iconName="delete"
              color={isDisabled ? undefined : 'light'}
            />
          </StylishNewButton>
        </button>
      </div>
      {expanded && (
        <div
          className="pratus-collapsible-item__content"
          style={{ backgroundColor: '#313639' }}
        >
          <div className="d-flex align-items-center justify-content-between gap-1 mb-2">
            Tactic
            <StylishNewButton onClick={() => setAdding(!adding)}>
              <SharedIcon iconName="add" classes="me-1" size="sm" />
              Add Tactic
            </StylishNewButton>
          </div>
          {adding && (
            <div className="bordered-card m-0 mb-2 mt-2 d-flex align-items-center gap-2">
              <StylishNewComboBox
                classes="flex-grow-1"
                options={TACTICS_LIBRARY}
                placeholder="Select or create Tactic..."
                customOptionLabel="+ Create New"
                onChange={(newValue) => {
                  const option = newValue as ComboBoxOption | null;
                  if (option) {
                    handleCreateTactic(option.label);
                    setAdding(false);
                  }
                }}
              />
              <StylishNewButton
                className="button--icon primary"
                onClick={() => setAdding(false)}
              >
                <SharedIcon iconName="close" />
              </StylishNewButton>
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  );
};
